import React, { useState } from 'react';
import { Container, Stack, Grid, Card, CardContent, Typography, Chip } from '@mui/material';
import { useNavigate, Navigate } from 'react-router-dom';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { toast } from 'react-toastify';

import { Page } from '../../../components';
import HeaderBreadcrumbs from '../../../components/header-breadcrumbs';
import { GT_COLORS } from '../../../constants/designTokens.constants';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { ImportUsersModal } from '../../../components/import-users-modal';
import { ImportDevicesModal } from '../../../components/import-devices-modal';
import { UserTypes } from '../../../types';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';

interface BulkActionCard {
    title: string;
    description: string;
    icon: React.ReactNode;
    enabled: boolean;
    onClick: () => void;
}

export const BulkActionsLandingPage: React.FC = () => {
    const navigate = useNavigate();
    const canAccess = permissionCheck(customerPermissionFlags.SUPER_ADMIN);

    // Modal state
    const [showImportUsersModal, setShowImportUsersModal] = useState(false);
    const [showImportDevicesModal, setShowImportDevicesModal] = useState(false);

    // Customer settings for ImportUsersModal
    const { getSetting, isLoading: isSettingsLoading } = useEffectiveSettings();
    const userUniqueIdentifier = getSetting(SETTING.userUniqueIdentifier, '');

    if (!canAccess) {
        return <Navigate to="/" replace />;
    }

    const bulkActions: BulkActionCard[] = [
        {
            title: 'Import POM Users',
            description: 'Bulk import new POM users via CSV upload',
            icon: <PersonAddAlt1Icon sx={{ fontSize: 32, color: GT_COLORS.green }} />,
            enabled: true,
            onClick: () => setShowImportUsersModal(true),
        },
        {
            title: 'Update POM Users',
            description: 'Bulk update POM user fields via CSV upload',
            icon: <SyncAltIcon sx={{ fontSize: 32, color: GT_COLORS.primary }} />,
            enabled: true,
            onClick: () => navigate('/bulk-actions/update-users'),
        },
        {
            title: 'Delete POM Users',
            description: 'Bulk delete POM users from your subscription via CSV upload',
            icon: <DeleteSweepIcon sx={{ fontSize: 32, color: GT_COLORS.red }} />,
            enabled: true,
            onClick: () => navigate('/bulk-actions/delete-users'),
        },
        {
            title: 'Import System Users',
            description: 'Bulk import system users (admins) via CSV upload',
            icon: <AdminPanelSettingsIcon sx={{ fontSize: 32, color: GT_COLORS.primary }} />,
            enabled: true,
            onClick: () => navigate('/system-users/bulk-upload'),
        },
        {
            title: 'Import Devices',
            description: 'Bulk import devices via CSV upload',
            icon: <DevicesOtherIcon sx={{ fontSize: 32, color: GT_COLORS.primary }} />,
            enabled: true,
            onClick: () => setShowImportDevicesModal(true),
        },
    ];

    return (
        <Page title="POM - Bulk Actions">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading="Bulk Actions"
                        links={[]}
                        activeLast={false}
                    />
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {bulkActions.map((action) => (
                        <Grid item xs={12} sm={6} md={4} key={action.title}>
                            <Card
                                sx={{
                                    height: '100%',
                                    cursor: action.enabled ? 'pointer' : 'default',
                                    opacity: action.enabled ? 1 : 0.5,
                                    border: `1px solid ${GT_COLORS.border}`,
                                    borderRadius: '10px',
                                    boxShadow: 'none',
                                    transition: 'box-shadow 0.2s, border-color 0.2s',
                                    ...(action.enabled && {
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            borderColor: GT_COLORS.primary,
                                        },
                                    }),
                                }}
                                onClick={() => action.enabled && action.onClick()}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                        {action.icon}
                                        {!action.enabled && (
                                            <Chip
                                                label="Coming Soon"
                                                size="small"
                                                sx={{
                                                    backgroundColor: GT_COLORS.badgeGrayBg,
                                                    color: GT_COLORS.badgeGrayColor,
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    height: 22,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                    <Typography
                                        fontWeight={600}
                                        fontSize={16}
                                        color={GT_COLORS.textPrimary}
                                        sx={{ mb: 0.5 }}
                                    >
                                        {action.title}
                                    </Typography>
                                    <Typography
                                        fontSize={13}
                                        color={GT_COLORS.textSecondary}
                                    >
                                        {action.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Import POM Users modal — reuses the same modal from the Users list page.
                    setShowActiveUsers / toggleRefresh / setToggleRefresh are no-ops here because
                    there's no users list to refresh on the landing page. The toast in onSuccess
                    provides feedback, and navigating to /users afterward loads the list fresh. */}
                <ImportUsersModal
                    visible={showImportUsersModal}
                    onClose={() => setShowImportUsersModal(false)}
                    onSuccess={(results: UserTypes.ImportExecuteResponse) => {
                        toast.success(`Import complete: ${results.created} user${results.created !== 1 ? 's' : ''} created.`);
                        setShowImportUsersModal(false);
                    }}
                    setShowActiveUsers={() => {}}
                    toggleRefresh={false}
                    setToggleRefresh={() => {}}
                    uniqueIdentifier={userUniqueIdentifier}
                    isCustomerSettingsLoading={isSettingsLoading}
                />

                {/* Import Devices modal */}
                <ImportDevicesModal
                    visible={showImportDevicesModal}
                    onClose={() => setShowImportDevicesModal(false)}
                />
            </Container>
        </Page>
    );
};
